import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase-config';
import {
  companyRootPath,
  resolveCompanyGeneralSettingsPath,
  resolveCompanyLiquidationSettingsPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyModulesSettingsPath,
  resolveCompanyOfficeSettingsPath,
  resolveCompanyPlanSettingsPath,
  resolveLogisticsMonitorSettingsPath,
} from './companyPaths';
import { ensureDefaultFleetExpenseCategories } from '../../modules/fleet/config/fleetExpenseCategoryCatalog';
import { buildModulesSettingsFromPlanKey } from '../../companyPlans';

const DEFAULT_COMPANY_ID = 'default';
const DEFAULT_COMPANY_NAME = 'Empresa Base';

const toTitleCase = (value = '') =>
  String(value)
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeCompanyId = (companyId = DEFAULT_COMPANY_ID) => {
  const value = String(companyId || DEFAULT_COMPANY_ID).trim();
  return value || DEFAULT_COMPANY_ID;
};

const resolveCompanyName = (companyId, customName) => {
  if (customName && String(customName).trim()) {
    return String(customName).trim();
  }

  if (companyId === DEFAULT_COMPANY_ID) {
    return DEFAULT_COMPANY_NAME;
  }

  return toTitleCase(companyId);
};

const normalizeModules = (modules = {}, planId = 'base') => {
  const normalized = { ...buildModulesSettingsFromPlanKey(planId) };

  Object.entries(modules || {}).forEach(([key, value]) => {
    normalized[key] = {
      ...(normalized[key] || {}),
      ...(value || {}),
    };
  });

  return normalized;
};

const normalizeBootstrapOptions = (companyIdOrOptions, maybeOptions = {}) => {
  if (typeof companyIdOrOptions === 'object' && companyIdOrOptions !== null) {
    const normalizedId = normalizeCompanyId(companyIdOrOptions.companyId);
    return {
      companyId: normalizedId,
      companyName: resolveCompanyName(normalizedId, companyIdOrOptions.companyName),
      mode: companyIdOrOptions.mode || (normalizedId === DEFAULT_COMPANY_ID ? 'single-company' : 'multi-company'),
      status: companyIdOrOptions.status || (normalizedId === DEFAULT_COMPANY_ID ? 'active' : 'trial'),
      planId: companyIdOrOptions.planId || 'trial',
      planName: companyIdOrOptions.planName || 'Plan Trial',
      active: companyIdOrOptions.active !== false,
      isSandbox: companyIdOrOptions.isSandbox ?? normalizedId !== DEFAULT_COMPANY_ID,
      modules: normalizeModules(companyIdOrOptions.modules, companyIdOrOptions.planId || 'trial'),
      office: companyIdOrOptions.office || {},
      liquidation: companyIdOrOptions.liquidation || {},
      logisticsMonitor: companyIdOrOptions.logisticsMonitor || {},
      main: companyIdOrOptions.main || {},
    };
  }

  const normalizedId = normalizeCompanyId(companyIdOrOptions);
  return {
    companyId: normalizedId,
    companyName: resolveCompanyName(normalizedId, maybeOptions.companyName),
    mode: maybeOptions.mode || (normalizedId === DEFAULT_COMPANY_ID ? 'single-company' : 'multi-company'),
    status: maybeOptions.status || (normalizedId === DEFAULT_COMPANY_ID ? 'active' : 'trial'),
    planId: maybeOptions.planId || 'trial',
    planName: maybeOptions.planName || 'Plan Trial',
    active: maybeOptions.active !== false,
    isSandbox: maybeOptions.isSandbox ?? normalizedId !== DEFAULT_COMPANY_ID,
    modules: normalizeModules(maybeOptions.modules, maybeOptions.planId || 'trial'),
    office: maybeOptions.office || {},
    liquidation: maybeOptions.liquidation || {},
    logisticsMonitor: maybeOptions.logisticsMonitor || {},
    main: maybeOptions.main || {},
  };
};

const buildRootDocument = (options) => ({
  companyId: options.companyId,
  companyName: options.companyName,
  responsibleName: '',
  contactEmail: '',
  contactPhone: '',
  officeName: options.companyName,
  locality: '',
  province: '',
  country: 'Argentina',
  fullAddress: '',
  active: options.active,
  isActive: options.active,
  status: options.status,
  mode: options.mode,
  plan: options.planName,
  planId: options.planId,
  planName: options.planName,
  isSandbox: options.isSandbox,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

const buildMainSettings = (options) => ({
  companyId: options.companyId,
  companyName: options.companyName,
  initialized: true,
  bootstrapVersion: 3,
  active: options.active,
  isActive: options.active,
  status: options.status,
  mode: options.mode,
  isSandbox: options.isSandbox,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
  ...options.main,
});

const buildGeneralSettings = (options) => ({
  companyId: options.companyId,
  companyName: options.companyName,
  responsibleName: '',
  contactEmail: '',
  contactPhone: '',
  active: options.active,
  isActive: options.active,
  status: options.status,
  mode: options.mode,
  plan: options.planName,
  planId: options.planId,
  planName: options.planName,
  isSandbox: options.isSandbox,
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

const buildModulesSettings = (options) => ({
  ...options.modules,
  updatedAt: serverTimestamp(),
});

const buildPlanSettings = (options) => ({
  planId: options.planId,
  planName: options.planName,
  status: options.status,
  active: options.active,
  isSandbox: options.isSandbox,
  autoManaged: false,
  activatedAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
});

const buildLiquidationSettings = (options) => ({
  enabled: true,
  inheritByDefault: true,
  currency: 'ARS',
  pricingMode: 'perKm',
  pricePerKm: 0,
  transportTypeRates: {},
  unitOverrides: {},
  lastManualUpdateAt: null,
  updatedAt: serverTimestamp(),
  ...options.liquidation,
});

const buildOfficeSettings = (options) => ({
  officeName: options.companyName,
  name: options.companyName,
  shortName: String(options.companyName || 'Base').trim().slice(0, 2).toUpperCase() || 'BA',
  street: '',
  streetNumber: '',
  floor: '',
  apartment: '',
  locality: '',
  province: '',
  postalCode: '',
  country: 'Argentina',
  fullAddress: '',
  address: '',
  reference: '',
  lat: null,
  lng: null,
  geocodingStatus: 'pending',
  allowManualRouteCreation: true,
  allowTransportAssignment: true,
  timezone: 'America/Argentina/Buenos_Aires',
  updatedAt: serverTimestamp(),
  ...options.office,
});

const buildLogisticsMonitorSettings = (options) => ({
  costModel: 'perKm',
  pricePerKm: Number(options.liquidation?.pricePerKm ?? 0) || 0,
  costScales: [],
  updatedAt: serverTimestamp(),
  ...options.logisticsMonitor,
});

const getMissingModuleEntries = (currentModules = {}, expectedModules = buildModulesSettingsFromPlanKey('base')) => {
  const missingEntries = {};

  Object.entries(expectedModules).forEach(([moduleKey, moduleValue]) => {
    if (!Object.prototype.hasOwnProperty.call(currentModules, moduleKey)) {
      missingEntries[moduleKey] = moduleValue;
    }
  });

  return missingEntries;
};

const ensureMissingDoc = async (ref, snapshot, data, writes, key) => {
  if (snapshot.exists()) return false;
  writes.push(setDoc(ref, data, { merge: true }));
  return key;
};

export const ensureCompanyBootstrap = async (
  companyIdOrOptions = DEFAULT_COMPANY_ID,
  maybeOptions = {}
) => {
  if (!db) {
    throw new Error('Firestore no está inicializado.');
  }

  const options = normalizeBootstrapOptions(companyIdOrOptions, maybeOptions);

  const refs = {
    root: doc(db, companyRootPath(options.companyId)),
    main: doc(db, resolveCompanyMainSettingsPath(options.companyId)),
    general: doc(db, resolveCompanyGeneralSettingsPath(options.companyId)),
    modules: doc(db, resolveCompanyModulesSettingsPath(options.companyId)),
    plan: doc(db, resolveCompanyPlanSettingsPath(options.companyId)),
    liquidation: doc(db, resolveCompanyLiquidationSettingsPath(options.companyId)),
    office: doc(db, resolveCompanyOfficeSettingsPath(options.companyId)),
    logisticsMonitor: doc(db, resolveLogisticsMonitorSettingsPath(options.companyId)),
  };

  const snapshots = await Promise.all(Object.values(refs).map((ref) => getDoc(ref)));
  const [rootSnap, mainSnap, generalSnap, modulesSnap, planSnap, liquidationSnap, officeSnap, logisticsMonitorSnap] = snapshots;

  const writes = [];
  const createdDocs = [];
  const patchedDocs = [];

  const createdRoot = await ensureMissingDoc(refs.root, rootSnap, buildRootDocument(options), writes, 'root');
  if (createdRoot) createdDocs.push(createdRoot);

  const createdMain = await ensureMissingDoc(refs.main, mainSnap, buildMainSettings(options), writes, 'settings/main');
  if (createdMain) createdDocs.push(createdMain);

  const createdGeneral = await ensureMissingDoc(refs.general, generalSnap, buildGeneralSettings(options), writes, 'settings/general');
  if (createdGeneral) createdDocs.push(createdGeneral);

  const createdModules = await ensureMissingDoc(refs.modules, modulesSnap, buildModulesSettings(options), writes, 'settings/modules');
  if (createdModules) {
    createdDocs.push(createdModules);
  } else {
    const currentModules = modulesSnap.data() || {};
    const missingModuleEntries = getMissingModuleEntries(currentModules, options.modules);

    if (Object.keys(missingModuleEntries).length > 0) {
      writes.push(
        setDoc(
          refs.modules,
          {
            ...missingModuleEntries,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        )
      );
      patchedDocs.push('settings/modules');
    }
  }

  const createdPlan = await ensureMissingDoc(refs.plan, planSnap, buildPlanSettings(options), writes, 'settings/plan');
  if (createdPlan) createdDocs.push(createdPlan);

  const createdLiquidation = await ensureMissingDoc(refs.liquidation, liquidationSnap, buildLiquidationSettings(options), writes, 'settings/liquidation');
  if (createdLiquidation) createdDocs.push(createdLiquidation);

  const createdOffice = await ensureMissingDoc(refs.office, officeSnap, buildOfficeSettings(options), writes, 'settings/office');
  if (createdOffice) createdDocs.push(createdOffice);

  const createdMonitor = await ensureMissingDoc(
    refs.logisticsMonitor,
    logisticsMonitorSnap,
    buildLogisticsMonitorSettings(options),
    writes,
    'settings/logisticsMonitor'
  );
  if (createdMonitor) createdDocs.push(createdMonitor);

  if (writes.length > 0) {
    await Promise.all(writes);
  }

  await ensureDefaultFleetExpenseCategories({
    db,
    companyId: options.companyId,
    companyName: options.companyName,
  });

  return {
    companyId: options.companyId,
    companyName: options.companyName,
    initialized: true,
    createdDocs,
    patchedDocs,
  };
};
